import { eventChannel } from 'redux-saga';
import {
  put,
  call,
  take,
  takeEvery,
  apply,
  takeLatest,
  select,
  delay,
} from 'redux-saga/effects';

import { getProductScope } from '@discover-frontend/utils';
import {
  getTokens,
  checkTokenExpired,
  getRefreshToken,
  setTokens,
} from 'src/utils/AuthHelperMethods';
import {
  CONVERSATION_STATUS_SUCCESS,
  CONVERSATION_STATUS_FAILURE,
  CONVERSATION_STATUS_REQUEST,
  CREATE_TSV_SUCCESS,
  TSV_STATUS_SUCCESS,
  TSV_STATUS_FAILURE,
  GET_ME_SUCCESS,
  SEND_WEBSOCKET_MESSAGE,
  STORE_WEBSOCKET_CONTAINER,
  GET_NOTIFICATIONS_FAILURE,
  RECEIVED_NOTIFICATION,
  TWITTER_CALLBACK_URL_STATUS_SUCCESS,
  TWITTER_CALLBACK_URL_STATUS_FAILURE,
} from '../constants/action-types';

import { API } from '../constants/config';
import * as api from '../services/cognitoApi';
import sagaFetch from './SagaFetch';

const encodeTokens = (tokens: { idToken: string; accessToken: string }) => {
  return `${tokens.idToken}SEP${tokens.accessToken}`;
};

const createEventChannel = (
  container: { socket: WebSocket | null; prev: WebSocket | null },
  browser_tab: string
) => {
  return eventChannel((emit) => {
    let reconnectInterval: NodeJS.Timeout | null = null;
    let pingInterval: NodeJS.Timeout | null = null;
    const reconnectIntervalTime = 50 * 60 * 1000;
    const pingIntervalTime = 8 * 60 * 1000;
    let reconnectOnErrorTime = 1000;
    const initializeWebSocket = (reconnect = false) => {
      const tokens = getTokens();
      const queryParams: Record<string, string> = {
        auth: encodeTokens(tokens),
        scope: getProductScope(),
      };
      if (reconnect) {
        queryParams.action = 'reconnect';
        queryParams.browser_tab = browser_tab;
      }
      const _socket = new WebSocket(
        `${API.WEBSOCKET}?${new URLSearchParams(queryParams).toString()}`
      );
      _socket.onopen = () => {
        if (container.prev) {
          container.prev.close();
        } else {
          emit('connected');
        }
        console.log('[SOCKET_DEBUG_LOG]: Websocket connected');
        pingInterval = setInterval(() => {
          _socket.send(JSON.stringify({ action: 'ping' }));
        }, pingIntervalTime);
        reconnectInterval = setInterval(() => {
          container.prev = container.socket;
          container.socket = initializeWebSocket(true);
          if (reconnectInterval) {
            clearInterval(reconnectInterval);
          }
          if (pingInterval) {
            clearInterval(pingInterval);
          }
        }, reconnectIntervalTime);
      };
      _socket.onmessage = (message) => {
        console.log(
          '[SOCKET_DEBUG_LOG]: Received message from websocket:',
          JSON.parse(message.data)
        );
        const decoded = JSON.parse(message.data);
        emit(decoded);
      };
      _socket.onerror = (e) => {
        reconnectOnErrorTime *= 2;
        console.log('[SOCKET_DEBUG_LOG]: Error', e);
        console.log(
          '[SOCKET_DEBUG_LOG]: Try to reconnect after',
          reconnectOnErrorTime,
          'ms'
        );
        setTimeout(() => {
          if (
            container.socket &&
            container.socket.readyState === WebSocket.OPEN
          ) {
            console.log(
              '[SOCKET_DEBUG_LOG]: Close previous connection before make a new one'
            );
            container.socket.close();
          }
          container.prev = null;
          container.socket = initializeWebSocket(true);
        }, reconnectOnErrorTime);
      };
      return _socket;
    };
    container.prev = null;
    container.socket = initializeWebSocket();

    return () => {
      container.socket.close();
      console.log('[SOCKET_DEBUG_LOG]: Close websocket');
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
      if (pingInterval) {
        clearInterval(pingInterval);
      }
    };
  });
};

function* initializeWebSocketChannel() {
  const browser_tab = yield select((state) => state.websocket.browser_tab);
  const container: { socket: WebSocket | null; prev: WebSocket | null } = {
    socket: null,
    prev: null,
  };
  const localManagedTypes = {
    conversation: {
      onSuccess: CONVERSATION_STATUS_SUCCESS,
      onError: CONVERSATION_STATUS_FAILURE,
    },
    tsv: {
      onSuccess: TSV_STATUS_SUCCESS,
      onError: TSV_STATUS_FAILURE,
    },
    notify_SHARE_QUERY: {
      onSuccess: RECEIVED_NOTIFICATION,
      onError: GET_NOTIFICATIONS_FAILURE,
    },
    notify_TWITTER_AUTH: {
      onSuccess: TWITTER_CALLBACK_URL_STATUS_SUCCESS,
      onError: TWITTER_CALLBACK_URL_STATUS_FAILURE,
    },
  };

  if (checkTokenExpired()) {
    const refreshToken = getRefreshToken();
    const { response } = yield sagaFetch(
      api.fetchRefreshedTokens,
      refreshToken
    );

    if (response.AuthenticationResult) {
      setTokens(
        response.AuthenticationResult.AccessToken,
        response.AuthenticationResult.IdToken,
        refreshToken
      );
    }
  }

  const tokens = getTokens();
  if (!tokens.accessToken || !tokens.idToken) {
    return;
  }
  const channel = yield call(createEventChannel, container, browser_tab);
  yield put({ type: STORE_WEBSOCKET_CONTAINER, payload: { container } });

  yield take(channel);
  yield put({
    type: SEND_WEBSOCKET_MESSAGE,
    message: {
      type: 'notify',
      action: 'subscribe',
      main_id: browser_tab,
      browser_tab,
      auth: encodeTokens(tokens),
      scope: getProductScope(),
    },
  });

  while (true) {
    const websocketResponse = yield take(channel);

    const type = websocketResponse.type;
    const notify_type = websocketResponse.notify_type;
    const statusCode = Number(websocketResponse.statusCode);

    if (type) {
      const actions =
        type === 'notify' && notify_type
          ? localManagedTypes[`${type}_${notify_type}`]
          : localManagedTypes[type];
      if (actions) {
        if (statusCode === 200) {
          yield put({
            type: actions.onSuccess,
            response: websocketResponse,
          });
        } else {
          yield put({
            type: actions.onError,
            error: websocketResponse.error,
          });
        }
      } else {
        window.dispatchEvent(
          new CustomEvent('FROM_WEBSOCKET', { detail: websocketResponse })
        );
      }
    }
  }
}

const createCustomEventChannel = () => {
  return eventChannel((emit) => {
    const onSendWebsocketMessage = (e: CustomEvent) => {
      emit(e.detail);
    };
    window.addEventListener('SEND_WEBSOCKET_MESSAGE', onSendWebsocketMessage);
    return () => {
      window.removeEventListener(
        'SEND_WEBSOCKET_MESSAGE',
        onSendWebsocketMessage
      );
    };
  });
};
function* initializeCustomEventChannel() {
  const tokens = getTokens();
  const channel = yield call(createCustomEventChannel);
  const browser_tab = yield select((state) => state.websocket.browser_tab);
  const wait = true;
  while (wait) {
    const customEventPayload = yield take(channel);
    yield put({
      type: SEND_WEBSOCKET_MESSAGE,
      message: {
        ...customEventPayload,
        browser_tab,
        auth: encodeTokens(tokens),
        scope: getProductScope(),
      },
    });
  }
}

function* sendWebSocketMessage(action: any) {
  const socketContainer = yield select((state) => state.websocket.container);
  let c = 1;
  const max = 3;
  console.log('[SOCKET_DEBUG_LOG]: Try to send with websocket', action);
  while (socketContainer.socket.readyState !== WebSocket.OPEN && c <= max) {
    c++;
    console.log(
      '[SOCKET_DEBUG_LOG]: Websocket not open, retry after',
      c * 1000,
      'ms'
    );
    yield delay(c * 1000);
  }
  console.log(
    '[SOCKET_DEBUG_LOG]: Websocket is',
    socketContainer.socket.readyState === WebSocket.OPEN ? 'open' : 'not open'
  );
  if (socketContainer.socket.readyState === WebSocket.OPEN) {
    console.log('[SOCKET_DEBUG_LOG]: Sending message');
    yield apply(socketContainer.socket, socketContainer.socket.send, [
      JSON.stringify(action.message),
    ]);
  }
}

function* sendConversationNotify(action: any) {
  const tokens = getTokens();
  const browser_tab = yield select((state) => state.websocket.browser_tab);
  yield put({
    type: SEND_WEBSOCKET_MESSAGE,
    message: {
      type: 'conversation',
      action: 'subscribe',
      main_id: action.membershipId,
      node_id: action.audienceId,
      browser_tab,
      auth: encodeTokens(tokens),
      scope: getProductScope(),
    },
  });
}

function* sendTsvNotify(action: any) {
  const tokens = getTokens();
  const browser_tab = yield select((state) => state.websocket.browser_tab);
  yield put({
    type: SEND_WEBSOCKET_MESSAGE,
    message: {
      type: 'tsv',
      action: 'subscribe',
      main_id: action.membershipId,
      node_id: action.audienceId,
      browser_tab,
      auth: encodeTokens(tokens),
      scope: getProductScope(),
    },
  });
}

export function* websocketActionWatcher() {
  yield takeLatest(GET_ME_SUCCESS, initializeWebSocketChannel);
  yield takeLatest(GET_ME_SUCCESS, initializeCustomEventChannel);
  yield takeEvery(SEND_WEBSOCKET_MESSAGE, sendWebSocketMessage);
  yield takeEvery(CONVERSATION_STATUS_REQUEST, sendConversationNotify);
  yield takeEvery(CREATE_TSV_SUCCESS, sendTsvNotify);
}
